// Poppy component

/**
 * Poppy is a special avatar with:
 * - Fixed brand colors (not player-based)
 * - Special hover behavior (hamburger menu, volume modal)
 * - Custom expressions and animations
 */

import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import Avatar, { AvatarProps, AvatarTransforms } from './Avatar';
import Icon from '@/components/common/Icon';
import { useAudioVisualization, useMedia } from '../../hooks';
import { FACE_BOX } from './configs/balloon';
import { useAnyDialogOpen } from '@/utilities/dialogPresenceStore';
import { getAvatarPalette, playerColorVars } from '../../styles/playerColors';

/**
 * Props for Poppy component
 * Omits player prop since Poppy uses fixed colors
 */
export interface PoppyProps extends Omit<
  AvatarProps,
  'player' | 'isPoppy' | 'transformRef'
> {
  // All Avatar props except player, isPoppy, and transformRef
  // isPoppy is automatically set to true
  // player is not needed since Poppy uses fixed colors
  // transformRef is managed internally

  /** Playback controls for Poppy's playback UI variant. */
  playbackState?: PlaybackPoppyState;
}

export type PlaybackPoppyState = {
  isPlaying: boolean;
  onToggle: () => void;
};

type GlowColorStyle = React.CSSProperties & {
  '--glow-color-dark': string;
  '--glow-color': string;
  '--glow-color-light': string;
};

/**
 * Poppy Component
 *
 * A specialized Avatar instance with brand colors and unique behaviors.
 * Automatically sets isPoppy=true and provides fixed color scheme.
 *
 * @param props - Poppy configuration (same as Avatar except player/isPoppy)
 * @returns Rendered Poppy avatar with brand styling
 */
export default function Poppy(props: PoppyProps) {
  const POPPY_PALETTE = getAvatarPalette('lime');
  const RED_PALETTE = getAvatarPalette('red');
  const { playbackState, ...avatarProps } = props;
  // Ref for the visualizer container
  const vizRef = useRef<HTMLDivElement>(null);
  // Ref for the menu icon container
  const menuRef = useRef<HTMLDivElement>(null);
  // Ref to receive Avatar's transform data
  const transformRef = useRef<AvatarTransforms>({
    position: { x: 120, y: 120 },
    rotation: { x: 0, y: 0, z: 0 },
    headTransform: { x: 0, y: 0, rotation: 0, scale: 1 },
    faceTransforms: {
      leftEye: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
      rightEye: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
      mouth: { shiftX: 0, shiftY: 0, scale: 1, rotation: 0 },
    },
  });

  // Get current volumes and mute state for dynamic glow behavior
  const {
    currentVolumes,
    isMuted: isGloballyMuted,
    webAudioReady,
  } = useMedia();
  // Initialize audio visualization analyzers only for Poppy when webAudio is ready
  const isAvatarAnimationPaused = useAnyDialogOpen();
  useAudioVisualization({
    containerRef: vizRef,
    enabled: webAudioReady && !isAvatarAnimationPaused,
  });

  const sizeValue = avatarProps.size ?? 240;
  const pxSize = typeof sizeValue === 'number' ? `${sizeValue}px` : sizeValue;
  const numericSize = typeof sizeValue === 'number' ? sizeValue : 240;

  // Track hover state from Avatar to control menu visibility
  const [isHovered, setIsHovered] = useState(false);

  // Handle hover at wrapper level when in music player mode to prevent flicker
  // (the icon overlay would otherwise cause mouseleave on the Avatar SVG)
  const handleMouseEnter = useCallback(() => {
    if (playbackState) {
      setIsHovered(true);
    }
  }, [playbackState]);

  const handleMouseLeave = useCallback(() => {
    if (playbackState) {
      setIsHovered(false);
    }
  }, [playbackState]);

  // Determine mouth icon based on music state.
  // - No playbackState: normal hamburger on hover
  // - Music paused: show play icon (triangle) always
  // - Music playing + hover: show pause icon
  // - Music playing + not hovered: no icon
  const mouthIcon = useMemo(() => {
    if (!playbackState) return isHovered ? 'hamburger' : null;
    if (!playbackState.isPlaying) return 'triangle';
    if (isHovered) return 'pause';
    return null;
  }, [playbackState, isHovered]);

  // Force hover appearance when in music mode and either paused or hovering
  // - Music paused: always show hover look (play icon visible)
  // - Music playing + hovering: show hover look (pause icon visible, mouth open)
  const forceHoveredAppearance = Boolean(
    playbackState && (!playbackState.isPlaying || isHovered)
  );

  // Determine glow colors and behavior based on speech volume AND global mute state
  const isMuted = currentVolumes.speechVolume === 0 || isGloballyMuted;
  const glowColorStyle: GlowColorStyle = isMuted
    ? {
        '--glow-color-dark': RED_PALETTE.dark,
        '--glow-color': RED_PALETTE.base,
        '--glow-color-light': RED_PALETTE.light,
      }
    : {
        // Keep unmuted halo dark and subtle.
        '--glow-color-dark': POPPY_PALETTE.dark,
        '--glow-color': POPPY_PALETTE.dark,
        '--glow-color-light': POPPY_PALETTE.dark,
      };
  // Update visualizer position using RAF to track Avatar's mouth
  useEffect(() => {
    if (isAvatarAnimationPaused) {
      return;
    }

    let rafId: number;

    const updateVisualizerPosition = () => {
      const { position, rotation, headTransform, faceTransforms } =
        transformRef.current;

      // Calculate scale factor (avatar size / viewBox size)
      const scaleFactor = numericSize / 240;

      // Calculate mouth center position in scaled coordinates
      // Mouth center is at FACE_BOX.mouth + (40, 20) in the mouth's local space
      // Avatar position is offset by -120 to center the 240x240 viewBox
      const mouthCenterX =
        (position.x -
          120 +
          headTransform.x +
          FACE_BOX.mouth.x +
          faceTransforms.mouth.shiftX +
          40) *
        scaleFactor;
      const mouthCenterY =
        (position.y -
          120 +
          headTransform.y +
          FACE_BOX.mouth.y +
          faceTransforms.mouth.shiftY +
          20) *
        scaleFactor;

      // Position visualizer so its center aligns with mouth center
      const vizSize = (64 / 240) * numericSize;
      const mouthX = mouthCenterX - vizSize / 2;
      const mouthY = mouthCenterY - vizSize / 2;

      const transformString = `
          translate(${mouthX}px, ${mouthY}px)
          rotateX(${rotation.x}deg)
          rotateY(${rotation.y}deg)
          rotateZ(${rotation.z}deg)
          rotate(180deg)
        `;

      if (vizRef.current) {
        vizRef.current.style.transform = transformString;
      }

      if (menuRef.current) {
        const menuSize = (40 / 240) * numericSize;
        const menuX = mouthCenterX - menuSize / 2;
        const menuY = mouthCenterY - menuSize / 2;

        menuRef.current.style.transform = `
          translate(${menuX}px, ${menuY}px)
          rotateX(${rotation.x}deg)
          rotateY(${rotation.y}deg)
          rotateZ(${rotation.z}deg)
          rotate(180deg)
        `;
      }

      rafId = requestAnimationFrame(updateVisualizerPosition);
    };

    updateVisualizerPosition();

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isAvatarAnimationPaused, numericSize]);

  return (
    <div
      style={{
        width: pxSize,
        height: pxSize,
        position: 'relative',
        // Set Poppy's fixed brand colors as CSS variables
        ...playerColorVars(POPPY_PALETTE),
        ...glowColorStyle,
      }}
      onMouseEnter={playbackState ? handleMouseEnter : undefined}
      onMouseLeave={playbackState ? handleMouseLeave : undefined}
    >
      <Avatar
        {...avatarProps}
        player={undefined} // Poppy doesn't use player-based colors
        isPoppy={true} // Enable Poppy-specific behavior
        enableGlow={!playbackState} // Disable glow when in music player mode
        glowType={isMuted ? 'pulse' : 'static'} // Pulse when muted, static otherwise
        forceHoveredAppearance={forceHoveredAppearance} // Force hover look when music paused
        transformRef={transformRef} // Pass ref to receive transform data
        onHoverStateChange={playbackState ? undefined : setIsHovered}
      />

      {/* Audio visualizer positioned over the mouth.
          Always mounted so the ref is stable; useAudioVisualization's `enabled`
          flag tears down / rebuilds the analyzers when the menu pauses animations. */}
      <div
        ref={vizRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${(64 / 240) * numericSize}px`,
          height: `${(64 / 240) * numericSize}px`,
          pointerEvents: 'none', // do not block avatar interactions
          transformOrigin: 'center center',
        }}
      />

      {/* Menu/play/pause icon positioned over the mouth */}
      {mouthIcon &&
        (playbackState || !avatarProps.disableMenu) &&
        !avatarProps.isReacting && (
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${(40 / 240) * numericSize}px`,
              height: `${(40 / 240) * numericSize}px`,
              pointerEvents: playbackState ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              animation: 'fadeInHamburger 0.6s ease-in-out forwards',
              transformOrigin: 'center center',
              zIndex: 5,
            }}
            onClick={playbackState?.onToggle}
          >
            <Icon
              name={mouthIcon}
              direction={mouthIcon === 'triangle' ? 'left' : undefined}
              size={(40 / 240) * numericSize}
              color={POPPY_PALETTE.base}
            />
          </div>
        )}

      <style>{`
        @keyframes fadeInHamburger {
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
